import type { ReactNode } from 'react'
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined'
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import { AUDIT_ENGAGEMENT_SECTIONS } from '@/lib/auditEngagements'
import { permissionCode, type ModuleCode } from '@/lib/permissions'
import { ROUTES } from '@/routes/paths'

/** A sub-item shown inside an expandable nav item's accordion. */
export interface NavChildItem {
  label: string
  to: string
  icon?: ReactNode
}

export interface NavItem {
  label: string
  to: string
  icon: ReactNode
  /** Permission required to see this item; omit for always-visible items. */
  permission?: string
  /** Only show once the account is approved (identity verified). */
  requiresApproval?: boolean
  /** Match the route exactly (used for the index route). */
  end?: boolean
  /**
   * Sub-items rendered as an accordion under this item. A parent with children
   * is a toggle, not a link: visitors pick a child, and `to` stays the module's
   * landing route for deep links and redirects.
   */
  children?: NavChildItem[]
}

export interface NavSection {
  title?: string
  items: NavItem[]
}

export interface ModuleNavItem {
  module: ModuleCode
  label: string
  to: string
  icon: ReactNode
  /** Accordion sub-items, when the module is split into sections. */
  children?: NavChildItem[]
}

/**
 * The sidebar modules, in approved order.
 *
 * Each entry becomes a nav item gated by `<module>:view` and a placeholder
 * route. Adding the real page later only means swapping the route element — the
 * sidebar and permissions already follow this list.
 */
export const MODULE_NAV_ITEMS: ModuleNavItem[] = [
  {
    module: 'audit_engagements',
    label: 'Audit Engagements',
    to: ROUTES.engagements,
    icon: <FactCheckOutlinedIcon />,
    // The module is worked section by section, so it expands in the sidebar
    // instead of opening a landing page of tiles.
    children: AUDIT_ENGAGEMENT_SECTIONS.map(({ label, to, icon: Icon }) => ({
      label,
      to,
      icon: <Icon fontSize="small" />,
    })),
  },
  { module: 'audit_plan', label: 'Audit Plan', to: ROUTES.auditPlan, icon: <EventNoteOutlinedIcon /> },
]

/**
 * The navigation tree.
 *
 * Items are filtered at render time against the caller's permission codes, so
 * the sidebar adapts to each role without branching on `role` directly — add an
 * item here and it appears for exactly the groups that hold its permission.
 * Every module is shown only when the caller has its View permission. Children
 * inherit their parent's gate: if the module is visible, all of its sections are.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Overview',
    items: [
      { label: 'Dashboard', to: ROUTES.dashboard, icon: <DashboardOutlinedIcon />, end: true, requiresApproval: true },
      ...MODULE_NAV_ITEMS.map<NavItem>((item) => ({
        label: item.label,
        to: item.to,
        icon: item.icon,
        children: item.children,
        permission: permissionCode(item.module, 'view'),
        requiresApproval: true,
      })),
    ],
  },
  {
    title: 'Administration',
    items: [
      {
        label: 'Users',
        to: ROUTES.staff,
        icon: <BadgeOutlinedIcon />,
        permission: permissionCode('users', 'view'),
        requiresApproval: true,
      },
      {
        label: 'Audit Trail',
        to: ROUTES.auditTrail,
        icon: <HistoryOutlinedIcon />,
        permission: permissionCode('users', 'view'),
        requiresApproval: true,
      },
    ],
  },
]

/**
 * Keep only sections/items the caller is allowed to see. Approval-gated items
 * are hidden until the account is active, so an account still in onboarding
 * only sees the account-status page.
 */
export function visibleNavSections(
  permissions: readonly string[],
  isApproved: boolean,
): NavSection[] {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) =>
        (!item.permission || permissions.includes(item.permission)) &&
        (!item.requiresApproval || isApproved),
    ),
  })).filter((section) => section.items.length > 0)
}
